/**
 * Redux-saga
 */
import { delay, put, takeEvery, call, select, takeLatest } from 'redux-saga/effects';

/**
 * Library
 */
import qs from 'qs';
import { AxiosResponse } from 'axios';

/**
 * Redux
 */
import {
  DOWNLOAD_LIBRARY,
  DOWNLOAD_LIBRARY_CLOUDFRONT,
  INSERT_LIBRARY,
  REQUEST_LIBRARY_COUNT,
  REQUEST_LIBRARY_LIST,
} from 'redux/actions/library/types';

import {
  requestLibraryListSuccess,
  requestLibraryListFailure,
  requestLibraryCountFailure,
  requestLibraryCountSuccess,
  insertLibraryFailure,
  insertLibrarySuccess,
  downloadLibrarySuccess,
  downloadLibraryFailure,
} from 'redux/actions/library/actions';
import { addWaitingLibraryJobId, throwError, enqueueSnackbar } from 'redux/actions/actions';

/**
 * Frameworks
 */
import { apiCall, getAPI, postAPI } from 'commons/framework/KeyFlowAPIRequest';
import KeyFlowResponse from 'commons/framework/KeyFlowResponse';
import { PaginationType } from 'commons/framework/type';
import { API } from 'commons/constants/KeyFlowAPI';
import {
  API_URL,
  Auth,
  LibraryStatusEnum,
  MoveToDownloadPageDelay,
} from 'commons/constants/KeyFlowConstants';
import HostFactory from 'commons/handler/HostFactory';

/**
 * Model
 */
import LibraryVO from 'commons/vo/Library';
import KeyFlowS3Client from 'commons/framework/aws/KeyFlowS3Client';
import ATagDownloadUtils from 'commons/utils/ATagDownloadUtils';
import S3AccessURL from 'commons/vo/S3AccessURL';
import { preflightRequest } from 'commons/utils/Preflight';
import { requestStatisticsLog } from 'redux/actions/statisticsLog/actions';
import StatisticsLogVO from 'commons/vo/param/StatisticsLogVO';

/**
 *
 * @param action {RequestLibraryListAction}
 */
function* requestLibraryListSaga(action: RequestLibraryListAction) {
  const { param } = action.payload;
  try {
    const response: AxiosResponse = yield call(getAPI, API.LIBRARY, param);
    const keyFlowResponse = new KeyFlowResponse(response.data);
    const resultList: LibraryVO[] = keyFlowResponse.getResult(LibraryVO) as LibraryVO[];
    const pagination: PaginationType = keyFlowResponse.page;
    const waitingCount: number = keyFlowResponse.waitingCount;
    yield put(requestLibraryListSuccess({ libraryList: resultList, pagination, waitingCount }));
  } catch (error: any | Error) {
    yield put(throwError({ error }));
    yield put(requestLibraryListFailure());
  }
}

function* requestLibraryCountSaga(action: RequestLibraryListAction) {
  const { param } = action.payload;
  try {
    const response: AxiosResponse = yield call(getAPI, API.LIBRARY, param);
    const keyFlowResponse = new KeyFlowResponse(response.data);
    const { itemCount } = keyFlowResponse.page;
    yield put(requestLibraryCountSuccess({ count: itemCount }));
  } catch (error: any | Error) {
    yield put(throwError({ error }));
    yield put(requestLibraryCountFailure());
  }
}

function* insertLibrarySaga(action: InsertLibraryAction) {
  const { param } = action.payload;
  console.debug({ libraryInsert: param });
  const userId = localStorage.getItem(Auth.USER_ID) || 'anonymous';

  // isClean 값이 없는 경우 false로 들어갈수있게 수정
  if (!param.isClean) {
    param.isClean = false;
  }

  try {
    const response: AxiosResponse = yield call(postAPI, API.LIBRARY, param);
    console.debug({ response });

    const keyFlowResponse = new KeyFlowResponse(response.data);
    const [firstResult] = keyFlowResponse.getResult();
    console.debug({ firstResult });

    const insertedLibrary = firstResult as unknown as { ID: number; METHOD: 'INSERT' };
    const id = insertedLibrary.ID as number;

    const logParam = new StatisticsLogVO({
      data: [{ ASSET_ID: param.videoId, VIDEO_LIBRARY_ID: id, TASK_TYPE: 'PARTIAL' }],
      user: userId,
    });

    yield put(addWaitingLibraryJobId({ addIdList: { [id]: LibraryStatusEnum.PROCEED }, userId }));
    yield put(insertLibrarySuccess());
    yield put(
      enqueueSnackbar({
        messages: ['부분인출 요청 성공.', '보관함에서 다운로드 받으세요.'],
        options: { variant: 'success' },
      }),
    );
    yield put(requestStatisticsLog({ param: logParam }));
  } catch (error: any | Error) {
    yield put(throwError({ error }));
    yield put(insertLibraryFailure());
    yield put(
      enqueueSnackbar({ messages: ['부분인출 요청 실패.'], options: { variant: 'error' } }),
    );
  }
}

function* downloadLibraryCloudfrontSaga(
  action: DownloadLibraryCloudfrontAction,
): Generator<
  import('redux-saga/effects').CallEffect<boolean> | import('redux-saga/effects').PutEffect,
  void,
  AxiosResponse
> {
  const { videoPath, videoId, id, filename } = action.payload;

  const downloadUtils = new ATagDownloadUtils();

  const url = new URL(videoPath, HostFactory.cloudfrontURL);

  const isOk = yield call(preflightRequest, url);

  const userId = localStorage.getItem(Auth.USER_ID) || 'anonymous';

  const logParam = new StatisticsLogVO({
    data: [{ ASSET_ID: videoId, VIDEO_LIBRARY_ID: id, TASK_TYPE: 'DOWNLOAD_PARTIAL' }],
    user: userId,
  });

  if (isOk) {
    yield put(requestStatisticsLog({ param: logParam }));

    const timeoutId = setTimeout(() => {
      url.search = qs.stringify({ download: true, filename });
      downloadUtils.setHref(url.toString()).download();
      downloadUtils.close();
    }, MoveToDownloadPageDelay);
    yield put(downloadLibrarySuccess());
  } else {
    yield put(throwError({ error: new Error('영상이 존재하지 않거나 연결에 실패하였습니다.') }));
    yield put(downloadLibraryFailure());
  }
}

function* downloadLibrarySaga(action: DownloadLibraryAction) {
  const { id, assetId } = action.payload;
  const param = {
    libraryId: id,
    assetId,
  };

  const downloadUtils = new ATagDownloadUtils();

  try {
    const response: AxiosResponse = yield call(getAPI, API.DOWNLOAD_URL_PARTIAL, param);
    const keyFlowResponse = new KeyFlowResponse<S3AccessURL>(response.data);
    const resultList: Array<S3AccessURL> = keyFlowResponse.result;
    const result = resultList[0];

    const downloadURL = result.PRESIGNED_URL;

    downloadUtils.setHref(downloadURL).download();
    downloadUtils.close();
    yield put(downloadLibrarySuccess());
  } catch (error: any | Error) {
    yield put(throwError({ error }));
    yield put(downloadLibraryFailure());
  }
}

/**
 * @deprecated
 * @param action
 */
function* downloadLibraryS3FromWebServerSaga(action: DownloadLibraryAction) {
  const { id, assetId } = action.payload;
  const param = {
    id,
    assetId,
  };

  const link = document.createElement('a');

  try {
    const accessToken = localStorage.getItem(Auth.ACCESS_TOKEN);
    const queryParam = qs.stringify({
      assetId,
      id,
      ACCESS_TOKEN: accessToken,
    });

    const href = `${HostFactory.apiURL}${API.LIBRARY_AWS_DOWNLOAD}?${queryParam}`;

    link.href = href;
    document.body.appendChild(link);
    link.click();
    yield put(downloadLibrarySuccess());
  } catch (error: any | Error) {
    yield put(throwError({ error }));
    yield put(downloadLibraryFailure());
  } finally {
    document.body.removeChild(link);
  }
}

export function* watchLibrarySaga() {
  // yield takeLatest(REQUEST_LIBRARY_LIST, requestMediaconvertJobListSaga);
  yield takeLatest(REQUEST_LIBRARY_LIST, requestLibraryListSaga);
  yield takeEvery(REQUEST_LIBRARY_COUNT, requestLibraryCountSaga);
  yield takeEvery(INSERT_LIBRARY, insertLibrarySaga);
  yield takeEvery(DOWNLOAD_LIBRARY, downloadLibrarySaga);
  yield takeEvery(DOWNLOAD_LIBRARY_CLOUDFRONT, downloadLibraryCloudfrontSaga);
}
